import { useState, useEffect, useMemo } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Calendar as CalIcon, Check, X as XIcon } from "lucide-react";
import { format, addDays, startOfWeek, isSameDay } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";

const HOURS = Array.from({ length: 12 }, (_, i) => i + 8);

export default function CalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAppt, setSelectedAppt] = useState(null);

  const weekStart = useMemo(() => startOfWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const loadAppointments = () => {
    setLoading(true);
    const start = format(weekDays[0], "yyyy-MM-dd");
    const end = format(weekDays[6], "yyyy-MM-dd");
    API.get(`/appointments?start_date=${start}&end_date=${end}`)
      .then(r => { setAppointments(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { loadAppointments(); }, [weekDays]); // eslint-disable-line react-hooks/exhaustive-deps

  const prevWeek = () => setCurrentDate(addDays(currentDate, -7));
  const nextWeek = () => setCurrentDate(addDays(currentDate, 7));
  const goToday = () => { setCurrentDate(new Date()); setSelectedDay(new Date()); };

  const getApptsForDay = (day) => {
    const dateStr = format(day, "yyyy-MM-dd");
    return appointments.filter(a => a.date === dateStr);
  };

  const updateStatus = async (id, status) => {
    try {
      await API.put(`/appointments/${id}/status?status=${status}`);
      toast.success(status === "completed" ? "RDV termin\u00e9" : status === "cancelled" ? "RDV annul\u00e9" : "Statut mis \u00e0 jour");
      setSelectedAppt(null);
      loadAppointments();
    } catch { toast.error("Erreur"); }
  };

  const selectedDayAppts = getApptsForDay(selectedDay).sort((a, b) => a.start_time.localeCompare(b.start_time));

  const statusColors = { confirmed: "bg-blue-500", completed: "bg-green-500", cancelled: "bg-red-400/60", no_show: "bg-yellow-500" };
  const statusLabels = { confirmed: "Confirm\u00e9", completed: "Termin\u00e9", cancelled: "Annul\u00e9", no_show: "Absent" };
  const statusBadgeColors = { confirmed: "bg-blue-100 text-blue-700", completed: "bg-green-100 text-green-700", cancelled: "bg-red-100 text-red-700", no_show: "bg-yellow-100 text-yellow-700" };

  return (
    <div data-testid="calendar-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Calendrier</h1>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="icon" onClick={prevWeek} className="rounded-full h-8 w-8" data-testid="prev-week-btn"><ChevronLeft size={16} /></Button>
          <Button variant="outline" size="sm" onClick={goToday} className="rounded-full text-xs h-8 px-3" data-testid="today-btn">Aujourd&apos;hui</Button>
          <Button variant="outline" size="icon" onClick={nextWeek} className="rounded-full h-8 w-8" data-testid="next-week-btn"><ChevronRight size={16} /></Button>
        </div>
      </div>

      <p className="text-xs sm:text-sm text-muted-foreground mb-4 capitalize">
        {format(weekDays[0], "d MMM", { locale: fr })} &mdash; {format(weekDays[6], "d MMM yyyy", { locale: fr })}
      </p>

      {/* Mobile: Day selector + Day view */}
      <div className="lg:hidden">
        {/* Day pills */}
        <div className="flex gap-1.5 mb-4 overflow-x-auto pb-2 -mx-1 px-1">
          {weekDays.map((day, i) => {
            const isToday = isSameDay(day, new Date());
            const isSelected = isSameDay(day, selectedDay);
            const count = getApptsForDay(day).length;
            return (
              <button key={i} onClick={() => setSelectedDay(day)}
                className={`flex flex-col items-center min-w-[52px] py-2.5 px-2 rounded-xl border transition-all ${isSelected ? "bg-[#2563EB] text-white border-[#2563EB]" : isToday ? "bg-blue-50 border-blue-200" : "bg-white border-border"}`}
                data-testid={`day-pill-${i}`}>
                <span className={`text-[10px] uppercase ${isSelected ? "text-white/60" : "text-muted-foreground"}`}>{format(day, "EEE", { locale: fr })}</span>
                <span className={`font-mono text-lg font-bold ${isSelected ? "text-white" : ""}`}>{format(day, "d")}</span>
                {count > 0 && <span className={`text-[9px] font-bold mt-0.5 ${isSelected ? "text-white/70" : "text-[#2563EB]"}`}>{count} rdv</span>}
              </button>
            );
          })}
        </div>

        {/* Selected day appointments */}
        <div className="bg-white rounded-xl border border-border">
          <div className="p-3 border-b border-border">
            <p className="text-sm font-semibold capitalize">{format(selectedDay, "EEEE d MMMM", { locale: fr })}</p>
          </div>
          {loading ? (
            <div className="p-4 space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
          ) : selectedDayAppts.length === 0 ? (
            <div className="p-8 text-center">
              <CalIcon size={24} className="text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Aucun rendez-vous</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {selectedDayAppts.map((appt) => (
                <button key={appt.id} onClick={() => setSelectedAppt(appt)} className="w-full p-3 flex items-center gap-3 hover:bg-accent/20 transition-colors text-left" data-testid={`mobile-appt-${appt.id}`}>
                  <div className={`w-1 h-12 rounded-full ${statusColors[appt.status]}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium truncate">{appt.client_name}</p>
                      <p className="font-mono text-xs font-bold shrink-0 ml-2">{appt.start_time}</p>
                    </div>
                    <div className="flex items-center justify-between mt-0.5">
                      <p className="text-[11px] text-muted-foreground truncate">{appt.service_name} &middot; {appt.price}&euro;</p>
                      <Badge className={`${statusBadgeColors[appt.status]} text-[9px] border-0 shrink-0 ml-2`}>
                        {statusLabels[appt.status]}
                      </Badge>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Desktop: Week Grid */}
      <div className="hidden lg:block bg-white rounded-xl border border-border overflow-hidden">
        <div className="grid grid-cols-[50px_repeat(7,1fr)] border-b border-border">
          <div className="p-2" />
          {weekDays.map((day, i) => {
            const isToday = isSameDay(day, new Date());
            const count = getApptsForDay(day).length;
            return (
              <div key={i} className={`p-3 text-center border-l border-border ${isToday ? "bg-blue-50/50" : ""}`}>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{format(day, "EEE", { locale: fr })}</p>
                <p className={`font-mono text-lg font-bold mt-0.5 ${isToday ? "text-[#2563EB]" : ""}`}>{format(day, "d")}</p>
                {count > 0 && <p className="text-[9px] text-[#2563EB] font-semibold">{count} rdv</p>}
              </div>
            );
          })}
        </div>
        {loading ? (
          <div className="p-8 flex justify-center"><Skeleton className="h-40 w-full" /></div>
        ) : (
          <div className="max-h-[560px] overflow-y-auto">
            {HOURS.map((hour) => (
              <div key={hour} className="grid grid-cols-[50px_repeat(7,1fr)] border-b border-border last:border-0">
                <div className="p-2 text-right pr-3"><span className="font-mono text-[10px] text-muted-foreground">{`${hour}:00`}</span></div>
                {weekDays.map((day, di) => {
                  const dayAppts = getApptsForDay(day).filter(a => parseInt(a.start_time.split(":")[0]) === hour);
                  return (
                    <div key={di} className="border-l border-border min-h-[52px] p-0.5 relative">
                      {dayAppts.map((appt) => (
                        <button key={appt.id} onClick={() => setSelectedAppt(appt)} className={`${statusColors[appt.status]} text-white rounded-md px-1.5 py-1 mb-0.5 cursor-pointer hover:opacity-90 transition-opacity w-full text-left`} title={`${appt.client_name} - ${appt.service_name}`} data-testid={`cal-appt-${appt.id}`}>
                          <p className="text-[9px] font-semibold truncate">{appt.client_name}</p>
                          <p className="text-[8px] opacity-70 truncate">{appt.start_time} &middot; {appt.service_name}</p>
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Appointment Detail Dialog */}
      <Dialog open={!!selectedAppt} onOpenChange={() => setSelectedAppt(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">D&eacute;tail du rendez-vous</DialogTitle></DialogHeader>
          {selectedAppt && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#2563EB]/10 flex items-center justify-center font-heading font-bold text-[#2563EB] text-lg">
                  {selectedAppt.client_name?.[0]?.toUpperCase()}
                </div>
                <div>
                  <p className="font-semibold">{selectedAppt.client_name}</p>
                  <p className="text-xs text-muted-foreground">{selectedAppt.client_phone}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-accent/50 rounded-lg p-3">
                  <p className="text-[10px] text-muted-foreground">Service</p>
                  <p className="font-medium text-xs mt-0.5">{selectedAppt.service_name}</p>
                </div>
                <div className="bg-accent/50 rounded-lg p-3">
                  <p className="text-[10px] text-muted-foreground">Horaire</p>
                  <p className="font-mono font-bold text-xs mt-0.5">{selectedAppt.start_time} - {selectedAppt.end_time}</p>
                </div>
                <div className="bg-accent/50 rounded-lg p-3">
                  <p className="text-[10px] text-muted-foreground">Prix</p>
                  <p className="font-mono font-bold text-xs mt-0.5">{selectedAppt.price}&euro;</p>
                </div>
                <div className="bg-accent/50 rounded-lg p-3">
                  <p className="text-[10px] text-muted-foreground">Statut</p>
                  <Badge className={`${statusBadgeColors[selectedAppt.status]} text-[9px] border-0 mt-0.5`}>{statusLabels[selectedAppt.status]}</Badge>
                </div>
              </div>
              {selectedAppt.status === "confirmed" && (
                <div className="flex gap-2 pt-2">
                  <Button size="sm" onClick={() => updateStatus(selectedAppt.id, "completed")} className="flex-1 rounded-full gap-1.5 bg-green-600 hover:bg-green-700 text-xs" data-testid="cal-complete-btn">
                    <Check size={14} /> Termin&eacute;
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => updateStatus(selectedAppt.id, "no_show")} className="rounded-full text-xs text-yellow-700 border-yellow-300 hover:bg-yellow-50" data-testid="cal-noshow-btn">
                    Absent
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => updateStatus(selectedAppt.id, "cancelled")} className="rounded-full text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid="cal-cancel-btn">
                    <XIcon size={14} />
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}